// The password sign-in (/portal). The same card as Masuk with Email and
// Kata sandi; the route is linked from no public page, carries
// <meta name="robots" content="noindex,nofollow"> while mounted (server.ts
// also sends X-Robots-Tag), and its title is the product name alone, so
// nothing on it names who signs in here. The API (POST /auth/login and its
// rate limit) and the role guards are unchanged: this only keeps the page
// out of sight, the guards keep it safe.
//   brand   card head (product name h1)
//   status  one notice: wrong credentials, too many attempts with the retry
//           time, a network failure, or the expired session
//   form    Email, Kata sandi with the reveal inside the field's trailing
//           edge (core.field_trailing_action), Masuk with its loading state
import { useEffect, useState, type FormEvent } from "react";
import * as API from "../api";
import { useAuth } from "../auth";
import { PRODUCT_NAME } from "../lib/product";
import { ThemeSwitch } from "../shell/ThemeToggle";
import { Field, fieldAria, Notice, PasswordInput, type NoticeTone } from "../ui/components";

export interface LoginErrors {
  email?: string;
  password?: string;
}

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function validateLogin(email: string, password: string): LoginErrors {
  const errors: LoginErrors = {};
  if (!email.trim()) errors.email = "Email wajib diisi.";
  else if (!EMAIL.test(email.trim())) errors.email = "Format email belum benar, contoh nama@instansi.go.id.";
  if (!password) errors.password = "Kata sandi wajib diisi.";
  return errors;
}

const clock = new Intl.DateTimeFormat("id-ID", { hour: "2-digit", minute: "2-digit", hour12: false });

/** "45 detik", "15 menit", "2 jam", "1 jam 30 menit". */
export function formatWait(seconds: number): string {
  const s = Math.max(1, Math.round(seconds));
  if (s < 60) return `${s} detik`;
  const minutes = Math.ceil(s / 60);
  if (minutes < 60) return `${minutes} menit`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest ? `${hours} jam ${rest} menit` : `${hours} jam`;
}

export interface LoginFailure {
  tone: NoticeTone;
  title: string;
  body: string;
}

/** The one notice a failed sign-in shows. */
export function describeLoginFailure(err: unknown, now: number = Date.now()): LoginFailure {
  const status = err instanceof API.ApiError ? err.status : 0;
  if (status === 401) {
    return {
      tone: "danger",
      title: "Email atau kata sandi salah",
      body: "Periksa kembali keduanya. Akun yang sedang dinonaktifkan juga tidak dapat masuk.",
    };
  }
  if (status === 429) {
    const wait = err instanceof API.ApiError ? err.retryAfter : null;
    return {
      tone: "warning",
      title: "Terlalu banyak percobaan masuk",
      body: wait
        ? `Demi keamanan akun, coba lagi dalam ${formatWait(wait)} (pukul ${clock.format(new Date(now + wait * 1000))}).`
        : "Demi keamanan akun, tunggu beberapa saat lalu coba lagi.",
    };
  }
  return { tone: "danger", title: "Belum bisa masuk", body: API.errorMessage(err) };
}

/** Adds <meta name="robots" content="noindex,nofollow"> and a neutral title while mounted. */
export function useHiddenPage(): void {
  useEffect(() => {
    const before = document.title;
    document.title = PRODUCT_NAME;
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex,nofollow";
    document.head.appendChild(meta);
    return () => {
      meta.remove();
      document.title = before;
    };
  }, []);
}

export function PortalScreen({ expired = false, onSubmit }: { expired?: boolean; onSubmit?: (email: string, password: string) => Promise<API.User> }) {
  useHiddenPage();
  const { signIn } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errors, setErrors] = useState<LoginErrors>({});
  const [failure, setFailure] = useState<LoginFailure | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (busy) return;
    const found = validateLogin(email, password);
    setErrors(found);
    setFailure(null);
    if (found.email || found.password) {
      const first = document.getElementById(found.email ? "login-email" : "login-password");
      first?.focus();
      return;
    }
    setBusy(true);
    try {
      const user = await (onSubmit ?? API.login)(email.trim(), password);
      signIn(user);
    } catch (err) {
      setFailure(describeLoginFailure(err));
      setBusy(false);
    }
  }

  return (
    <main id="main" className="login" data-ui-shell="none" data-jal-shell="none">
      <div className="login-column">
        <section className="card login-card" aria-labelledby="login-title">
          <header className="login-head">
            <div className="login-head-text">
              <h1 id="login-title" className="login-title">
                {PRODUCT_NAME}
              </h1>
              <p className="login-lead">Masuk dengan email dan kata sandi akun Anda.</p>
            </div>
          </header>

          <div className="login-body">
            {failure ? (
              <Notice tone={failure.tone} title={failure.title} className="login-notice">
                <p>{failure.body}</p>
              </Notice>
            ) : expired ? (
              <Notice tone="info" title="Sesi Anda berakhir" className="login-notice">
                <p>Silakan masuk kembali untuk melanjutkan pekerjaan Anda.</p>
              </Notice>
            ) : null}

            <form className="login-form" noValidate aria-label="Masuk" onSubmit={submit}>
              <Field id="login-email" label="Email" error={errors.email}>
                <input
                  {...fieldAria("login-email", errors.email)}
                  type="email"
                  name="email"
                  autoComplete="username"
                  inputMode="email"
                  autoCapitalize="none"
                  spellCheck={false}
                  placeholder="nama@instansi.go.id"
                  value={email}
                  onChange={(e) => setEmail(e.currentTarget.value)}
                  readOnly={busy}
                />
              </Field>
              <Field id="login-password" label="Kata sandi" error={errors.password}>
                <PasswordInput
                  {...fieldAria("login-password", errors.password)}
                  name="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.currentTarget.value)}
                  readOnly={busy}
                />
              </Field>
              <button type="submit" className="btn btn-block" aria-busy={busy || undefined}>
                {busy ? "Memeriksa akun" : "Masuk"}
              </button>
            </form>
          </div>
        </section>
        <div className="login-tools">
          <ThemeSwitch />
        </div>
      </div>
    </main>
  );
}
